import React, { useEffect, useState } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { TrendingUp, Users, Share2, Target, RefreshCw } from "lucide-react";

export default function AdminGoalInsights() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/goal-insights");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return <AppShell><div className="cq-in max-w-6xl"><p className="text-slate-400">Loading…</p></div></AppShell>;
  }
  if (!data) return null;

  const maxCat = Math.max(1, ...data.by_category.map((c) => c.count));

  return (
    <AppShell>
      <div className="cq-in max-w-6xl">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] font-bold text-amber-600">Admin · Insights</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1">Team Goal Insights</h1>
            <p className="text-slate-600 mt-1 max-w-2xl">
              Silent, aggregated view of what our clients are chasing — pitch smarter services
              without pestering anyone.
            </p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-bold text-slate-700 border border-slate-200 hover:bg-slate-50"
            data-testid="goal-insights-refresh"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI icon={<Target size={16} />} label="Active client goals" value={data.total_goals} accent="text-amber-600" />
          <KPI icon={<TrendingUp size={16} />} label="Changes · 7d" value={data.changes_last_7d} accent="text-emerald-600" />
          <KPI icon={<Users size={16} />} label="Changes · 30d" value={data.changes_last_30d} accent="text-slate-900" />
          <KPI icon={<Share2 size={16} />} label="Vision shares · 30d" value={data.shares_last_30d} accent="text-fuchsia-600" />
        </div>

        {/* Category distribution */}
        <Card className="mt-6 p-6 rounded-2xl border-slate-100">
          <h2 className="font-display text-2xl font-black text-slate-900 mb-4">What clients are chasing</h2>
          {data.by_category.length === 0 && (
            <p className="text-sm text-slate-400">No goals set yet.</p>
          )}
          <div className="space-y-2" data-testid="goal-insights-categories">
            {data.by_category.map((c) => (
              <div key={c.category} className="flex items-center gap-3">
                <div className="w-40 shrink-0 flex items-center gap-2">
                  <span className="text-lg">{c.emoji}</span>
                  <p className="text-sm font-bold text-slate-800">{c.label}</p>
                </div>
                <div className="flex-1 h-6 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full rounded-full flex items-center justify-end px-2 text-[10px] font-black text-white"
                    style={{ width: `${Math.max(6, (c.count / maxCat) * 100)}%`, background: c.primary }}
                  >
                    {c.count}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Share channels */}
        {data.share_channels?.length > 0 && (
          <Card className="mt-4 p-6 rounded-2xl border-slate-100">
            <h2 className="font-display text-xl font-black text-slate-900 mb-3">Where clients are sharing</h2>
            <div className="flex flex-wrap gap-2">
              {data.share_channels.map((s) => (
                <span key={s.channel} className="inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-bold uppercase tracking-widest">
                  {s.channel} · {s.count}
                </span>
              ))}
            </div>
          </Card>
        )}

        {/* Recent goals table */}
        <Card className="mt-6 p-6 rounded-2xl border-slate-100">
          <h2 className="font-display text-2xl font-black text-slate-900 mb-4">Recent goals</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest font-black text-slate-500 border-b border-slate-100">
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Category</th>
                  <th className="py-2 pr-3">Goal</th>
                  <th className="py-2 pr-3">Shares</th>
                  <th className="py-2 pr-3">Updated</th>
                </tr>
              </thead>
              <tbody data-testid="goal-insights-recent">
                {data.recent.map((r) => (
                  <tr key={r.user_id} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="py-3 pr-3">
                      <p className="font-bold text-slate-900">{r.client_name || "—"}</p>
                      <p className="text-[11px] text-slate-500">{r.client_email}</p>
                    </td>
                    <td className="py-3 pr-3">
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest"
                            style={{ background: `${r.primary}22`, color: r.primary }}>
                        {r.emoji} {r.category_label}
                      </span>
                    </td>
                    <td className="py-3 pr-3 max-w-md">
                      <p className="font-bold text-slate-800">{r.goal_label}</p>
                      <p className="text-[11px] text-slate-500 line-clamp-1">{r.goal_text}</p>
                    </td>
                    <td className="py-3 pr-3">
                      <span className={`text-xs font-bold ${r.shares_total > 0 ? "text-fuchsia-600" : "text-slate-400"}`}>
                        {r.shares_total || 0}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-xs text-slate-500 whitespace-nowrap">
                      {r.updated_at ? new Date(r.updated_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
                {data.recent.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400 text-sm">No goals yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

function KPI({ icon, label, value, accent }) {
  return (
    <Card className="p-4 rounded-2xl border-slate-100">
      <div className={`inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest ${accent}`}>
        {icon} {label}
      </div>
      <p className={`font-display text-3xl font-black mt-1 ${accent}`}>{value ?? 0}</p>
    </Card>
  );
}
